/**
 * Dialog: resultado de la generación del archivo TEF (Banco de Chile).
 * Muestra el mensaje, los documentos incluidos y — sobre todo — los omitidos
 * por falta de datos bancarios en "Cuentas", para que Carlos sepa qué corregir.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { AlertTriangle, CheckCircle2, Landmark, Download } from 'lucide-react';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

export function TefResultDialog({ result, onCancel, onDownload }) {
  const open = !!result;
  const incluidas = result?.incluidas || [];
  const omitidas = result?.omitidas || [];
  const puedeDescargar = (result?.num_archivos || 0) > 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="dialog-tef-result">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-indigo-700" /> Archivo TEF · {result?.nombre || 'Nómina'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground" data-testid="tef-mensaje">{result?.mensaje}</p>

        {result?.num_archivos > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-400" data-testid="tef-descarga-nota">
            <Download className="h-3.5 w-3.5 shrink-0" />
            El archivo se descargará, por defecto, en tu carpeta de <b>Descargas</b>.
          </p>
        )}

        <ScrollArea className="max-h-[50vh] pr-3">
          {incluidas.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 mb-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Incluidas ({incluidas.length})
              </div>
              <ul className="space-y-1" data-testid="tef-incluidas">
                {incluidas.map((f, i) => (
                  <li key={`inc-${i}`} className="text-xs flex items-center justify-between gap-2 rounded bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1">
                    <span className="truncate">
                      <b>FE-{f.folio}</b> · {f.proveedor || f.rut}
                    </span>
                    <span className="shrink-0 text-muted-foreground tabular-nums">
                      {fmt(f.monto)} · {f.tipo_op}{f.lineas > 1 ? ` (${f.lineas} líneas)` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {omitidas.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700 mb-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Omitidas ({omitidas.length}) — corrige en Cuentas Bancarias de Proveedores ▸ Cuentas
              </div>
              <ul className="space-y-1" data-testid="tef-omitidas">
                {omitidas.map((f, i) => (
                  <li key={`om-${i}`} className="text-xs rounded bg-red-50 dark:bg-red-950/30 px-2 py-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate"><b>FE-{f.folio}</b> · {f.proveedor || f.rut}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">{fmt(f.monto)}</span>
                    </div>
                    <div className="text-red-700 dark:text-red-400">{f.motivo}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onCancel} data-testid="tef-result-cancel">Cancelar</Button>
          <Button size="sm" onClick={onDownload} disabled={!puedeDescargar} className="gap-1.5" data-testid="tef-result-download">
            <Download className="h-4 w-4" /> Descargar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
